import React, { useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Dna, RefreshCw, X, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { trackEvent } from "./GA4Tracker";
import { toast } from "sonner";
import EpigeneticMapInputForm from "./EpigeneticMapInputForm";
import EpigeneticMapVisualization from "./EpigeneticMapVisualization";
import EpigeneticMapInsights from "./EpigeneticMapInsights";

const getToken = () =>
  localStorage.getItem("nutramilo_access_token") ||
  sessionStorage.getItem("nutramilo_access_token");

/**
 * EpigeneticMapPanel — главен wrapper.
 * Pro + Trial only — gating е през UpgradeLock в parent (AnalysesPage).
 * iter321 — приема `targetUserId` за doctor-side използване (изпраща
 * target_user_id в /calculate body и /history query param).
 */
export default function EpigeneticMapPanel({ targetUserId = null } = {}) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pathwayInfo, setPathwayInfo] = useState(null);
  const [pathwayLoading, setPathwayLoading] = useState(false);
  // iter351.G: collapsible panel — closed by default to save vertical space.
  const [expanded, setExpanded] = useState(false);

  const handlePathwayClick = useCallback(async (pathwayId) => {
    setPathwayLoading(true);
    try {
      const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
      const { data } = await axios.get(`${API}/epigenetic-map/pathway-info/${pathwayId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPathwayInfo(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || (bg ? "Не успях да заредя информация." : "Could not load info.");
      toast.error(msg);
    } finally {
      setPathwayLoading(false);
    }
  }, [API, bg]);

  const handleSubmit = useCallback(async (inputs) => {
    setLoading(true);
    try {
      const token = getToken();
      const reqBody = { ...inputs, language: bg ? "bg" : "en", with_narrative: true, save: true };
      if (targetUserId) reqBody.target_user_id = targetUserId;
      const { data } = await axios.post(
        `${API}/epigenetic-map/calculate`,
        reqBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMap(data);
      trackEvent("epigenetic_map_calculated", {
        lifestyle_band: data?.lifestyle_score?.band,
        hidden_risks_count: data?.hidden_risks?.length || 0,
        earned_badges: (data?.earned_badges || []).length,
      });
      toast.success(bg ? "Картата е генерирана." : "Map generated.");
    } catch (e) {
      const msg = e?.response?.data?.detail || (bg ? "Грешка при изчисляването." : "Calculation failed.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [API, bg, targetUserId]);

  const handleReset = () => setMap(null);

  return (
    <div className="space-y-4" data-testid="epigenetic-map-panel">
      <div className={`rounded-2xl p-4 border ${
        isDark
          ? "bg-gradient-to-br from-violet-500/10 to-pink-500/10 border-violet-500/20"
          : "bg-gradient-to-br from-violet-50 to-pink-50 border-violet-100"
      }`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-violet-600 flex items-center justify-center flex-shrink-0">
            <Dna className="w-5 h-5 text-white" />
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex-1 min-w-0 text-left"
            data-testid="epimap-toggle"
            aria-expanded={expanded}
          >
            <h2 className="text-base font-bold flex items-center gap-2">
              {bg ? "Епигенетична карта" : "Epigenetic Map"}
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-200 dark:bg-violet-500/30 text-violet-800 dark:text-violet-200">
                Pro
              </span>
            </h2>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              {bg
                ? "Lifestyle + фамилна анамнеза + диагнози + лаб. → молекулярни пътища → скрити рискове + превенция."
                : "Lifestyle + family history + diagnoses + labs → molecular pathways → hidden risks + prevention."}
            </p>
          </button>
          <div className="flex items-center gap-1.5 shrink-0">
            {map && expanded && (
              <button
                onClick={handleReset}
                className={`text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border ${
                  isDark
                    ? "border-white/10 hover:bg-white/5"
                    : "border-gray-200 hover:bg-white"
                }`}
                data-testid="epimap-reset"
              >
                <RefreshCw className="w-3 h-3" />
                {bg ? "Нова карта" : "New map"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? (bg ? "Затвори" : "Close") : (bg ? "Отвори" : "Open")}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isDark ? "hover:bg-white/10 text-gray-300" : "hover:bg-violet-100 text-violet-700"
              }`}
              data-testid="epimap-collapse-btn"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {expanded && (!map ? (
        <EpigeneticMapInputForm onSubmit={handleSubmit} loading={loading} />
      ) : (
        <>
          <EpigeneticMapVisualization data={map.sankey} onPathwayClick={handlePathwayClick} />
          <EpigeneticMapInsights map={map} />
        </>
      ))}

      {/* Pathway info popup (Reactome/KEGG) */}
      {(pathwayInfo || pathwayLoading) && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setPathwayInfo(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`max-w-md w-full rounded-2xl p-5 ${
              isDark ? "bg-[hsl(223,47%,10%)] border border-white/10" : "bg-white border border-gray-200"
            }`}
            data-testid="epimap-pathway-popup"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold">{bg ? "Информация за пътя" : "Pathway info"}</h3>
              <button onClick={() => setPathwayInfo(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-white/5 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            {pathwayLoading ? (
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>{bg ? "Зарежда..." : "Loading..."}</p>
            ) : pathwayInfo ? (
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{bg ? "Източник" : "Source"}:</span>
                  <span className="ml-2 text-xs uppercase">{pathwayInfo.source}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{bg ? "Име" : "Name"}:</span>
                  <p className="text-sm font-semibold">{pathwayInfo.display_name}</p>
                </div>
                {pathwayInfo.summary && (
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {pathwayInfo.summary}
                  </p>
                )}
                <a
                  href={pathwayInfo.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-violet-500 hover:underline mt-2"
                >
                  <ExternalLink className="w-3 h-3" />
                  {bg ? "Отвори пълна диаграма" : "Open full diagram"}
                </a>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
